import React, { useCallback, useEffect, useRef } from 'react';
import {
  useBeforeUnload,
  useBlocker,
} from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';
import { scrollToChangedElement } from '../../../shared/hooks/useUnsavedChanges';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useAdminExhibitionStore } from '../../share/store/adminExhibitionStore';

const ExhibitionRouteGuard: React.FC = () => {
  const setUnchangedAlert = useStandardAlertStore(
    (state) => state.setUnchangedAlert,
  );
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => (
      currentLocation.pathname !== nextLocation.pathname
      && useAdminExhibitionStore.getState().hasChanged
    ),
    [],
  );
  const blocker = useBlocker(shouldBlock);
  const blockerRef = useRef(blocker);
  const isAlertOpenRef = useRef(false);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      isAlertOpenRef.current = false;
      return;
    }
    if (isAlertOpenRef.current) return;

    isAlertOpenRef.current = true;
    setTimeout(scrollToChangedElement, 100);
    setUnchangedAlert(
      () => {
        const current = blockerRef.current;
        if (current.state === 'blocked') current.proceed();
      },
      () => {
        const current = blockerRef.current;
        if (current.state === 'blocked') current.reset();
      },
    );
  }, [blocker.state, setUnchangedAlert]);

  useBeforeUnload(
    useCallback((event) => {
      if (!useAdminExhibitionStore.getState().hasChanged) return;
      event.preventDefault();
      event.returnValue = '';
    }, []),
    { capture: true },
  );

  return null;
};

export default ExhibitionRouteGuard;
